import React, { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
    Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel,
    IconButton, Paper, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { InputField } from '@/pages/components/common/InputField';
import { DatePicker } from '@/pages/components/common/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTheme } from '@mui/material/styles';
import {
    fetchColumnDefs, fetchGlSheetPage, fetchGlImportCache, saveGlPreview,
    GlColumnDef, GlImportResultVO, buildColumnKey,
} from '@/services/accountService/glInvoiceService';
import {
    useGlInvoiceCreateCtx, makeEmptyRow, makeEmptyDrCrItem, rowFromGlSheet, splitDrCrSides,
} from '@/pages/components/account/gl-invoice/create-stepper/context';
import type { InvoiceEntryRow, DrCrItem } from '@/pages/components/account/gl-invoice/create-stepper/context';
import { isFxOriginalColumn, stripDupSuffix } from '@/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import { writeDrCrColumnValues } from '@/utils/glSheetEditHelpers';
import { useColumnZhLookup } from '@/pages/components/account/gl-invoice/columnZhFallback';
import { ColumnSearchSelect, DR_CR_LINE_HEIGHT } from '@/pages/components/account/gl-invoice/ColumnSearchSelect';
import {
    collectInvoiceFieldHistory,
    HistorySuggestField,
    type InvoiceFieldHistory,
} from '@/pages/components/account/gl-invoice/HistorySuggestField';
import {
    autoFillOppositeAmount,
    balanceRowDrCrItems,
    coerceAmountInputBySide,
    computeRowDrCrBalance,
    copyDrCrAccountsFromRow,
    duplicateInvoiceEntryRow,
    filterColumnOptionsBySide,
    findLastRowWithAccounts,
    formatAmountInput,
    normalizeAmountOnBlur,
    shouldAdvanceAfterAmountBlur,
    shouldAdvanceAfterCrAccountSelect,
} from '@/pages/components/account/gl-invoice/mode/glEditEntryHelpers';
import { fetchLeafGlCategories, type GlCategory } from '@/services/accountService/glCategoryService';
import { listAccountsByCompany, type GlAccount } from '@/services/accountService/companyGLCategoryService';
import {
    buildDrCrColumnMapFromGlAndCoGl,
    buildDrCrColumnOptions,
} from '@/pages/components/account/gl-invoice/drCrColumnOptions';
import GlColumnLabel from '@/pages/components/account/gl-invoice/GlColumnLabel';
import { useShowClientAlias } from '@/pages/components/account/gl-invoice/useShowClientAlias';
import { normalizeToDdMmYyyy } from '@/utils/dataUtils';

export interface StepCInvoiceEntryHandle {
    triggerSave: () => void;
}

interface StepCInvoiceEntryProps {
    companyId: number;
    annualId: number;
    importToken?: string | null;
    onSaveSuccess: (result: GlImportResultVO) => void;
    onSaveError: (msg: string) => void;
    saving: boolean;
    onSavingChange: (v: boolean) => void;
}

function focusNextEditField(rowEl: HTMLElement | null, current: Element | null, backward = false): boolean {
    if (!rowEl) return false;
    const nodes = Array.from(
        rowEl.querySelectorAll<HTMLElement>('input:not([disabled]), textarea:not([disabled]), button:not([disabled])'),
    ).filter((el) => {
        if (el.getAttribute('data-edit-skip') === '1') return false;
        if (el.tagName === 'BUTTON' && el.getAttribute('data-edit-nav') !== '1') return false;
        return el.offsetParent != null;
    });
    if (nodes.length === 0) return false;
    const idx = current ? nodes.findIndex((n) => n === current || n.contains(current)) : -1;
    const nextIdx = backward
        ? (idx <= 0 ? nodes.length - 1 : idx - 1)
        : (idx < 0 ? 0 : idx + 1);
    if (!backward && idx >= 0 && idx >= nodes.length - 1) return false;
    nodes[nextIdx]?.focus();
    if (nodes[nextIdx] instanceof HTMLInputElement) nodes[nextIdx].select?.();
    return true;
}

/** 行数少时全量渲染，避免虚拟列表低估高度导致滚不到最后一行（如 Chi Ying #58） */
const VIRTUALIZE_THRESHOLD = 150;
const VIRTUAL_OVERSCAN = 6;
const COMPACT_ROW_BASE = 36;
const COMPACT_DR_CR_LINE = 22;

interface ColumnOption {
    key: string;
    label: string;
}

function estimateRowHeight(row: InvoiceEntryRow | undefined): number {
    if (!row) return 80;
    const { dr, cr } = splitDrCrSides(row.drCrItems);
    const lines = Math.max(
        dr.filter((i) => row.drCrItems[i]?.columnKey || row.drCrItems[i]?.amount).length,
        cr.filter((i) => row.drCrItems[i]?.columnKey || row.drCrItems[i]?.amount).length,
        1,
    );
    return COMPACT_ROW_BASE + lines * COMPACT_DR_CR_LINE;
}

function cellText(value: string | undefined | null, placeholder = '—'): string {
    const v = (value ?? '').trim();
    return v || placeholder;
}

// ===================== Compact row (scroll-friendly, no TextField) =====================

interface InvoiceRowCompactProps {
    row: InvoiceEntryRow;
    rowIdx: number;
    columnMap: Map<string, GlColumnDef>;
    resolveFallbackZh: (def: GlColumnDef) => string;
    onEdit: (rowKey: string) => void;
    onRemoveRow: (key: string) => void;
    canDelete: boolean;
    showClientAlias: boolean;
    isAliasVisible: (columnKey?: string | null) => boolean;
    onToggleRevealAlias: (columnKey: string) => void;
}

const InvoiceRowCompact = React.memo(function InvoiceRowCompact({
    row, rowIdx, columnMap, resolveFallbackZh, onEdit, onRemoveRow, canDelete,
    showClientAlias, isAliasVisible, onToggleRevealAlias,
}: InvoiceRowCompactProps) {
    const hasWarnings = !!(row.validationWarnings && row.validationWarnings.length > 0);
    return (
        <TableRow
            data-index={rowIdx}
            onClick={() => onEdit(row.key)}
            sx={{
                cursor: 'pointer',
                ...(hasWarnings ? { bgcolor: 'warning.50' } : {}),
                '&:hover': { bgcolor: hasWarnings ? 'warning.100' : 'action.hover' },
            }}
        >
            <TableCell sx={{ color: 'text.secondary', fontSize: 12, py: 0.5 }}>
                {rowIdx + 1}
                {hasWarnings && (
                    <WarningAmberIcon sx={{ fontSize: 12, color: 'warning.main', ml: 0.25, verticalAlign: 'middle' }} />
                )}
            </TableCell>
            <TableCell sx={{ fontSize: 13, py: 0.5, whiteSpace: 'nowrap' }}>{cellText(row.date)}</TableCell>
            <TableCell sx={{ fontSize: 13, py: 0.5, maxWidth: 160 }} title={row.description ?? ''}>
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cellText(row.description)}
                </Box>
            </TableCell>
            <TableCell sx={{ fontSize: 13, py: 0.5 }}>{cellText(row.voucherNo, '')}</TableCell>
            <TableCell sx={{ fontSize: 13, py: 0.5 }}>{cellText(row.invoiceNo, '')}</TableCell>
            <TableCell sx={{ fontSize: 13, py: 0.5 }}>{cellText(row.invoiceDate, '')}</TableCell>
            <TableCell sx={{ fontSize: 13, py: 0.5 }}>{cellText(row.chequeNo, '')}</TableCell>
            <TableCell sx={{ fontSize: 13, py: 0.5, maxWidth: 140 }} title={row.details ?? ''}>
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cellText(row.details, '')}
                </Box>
            </TableCell>
            {(['dr', 'cr'] as const).map((side) => {
                const indexes = splitDrCrSides(row.drCrItems)[side]
                    .filter((idx) => row.drCrItems[idx]?.columnKey || row.drCrItems[idx]?.amount);
                const lineSx = { fontSize: 13, lineHeight: '22px', height: 22 } as const;
                return (
                    <React.Fragment key={side}>
                        <TableCell sx={{ py: 0.5 }} onClick={(e) => e.stopPropagation()}>
                            {indexes.map((idx) => {
                                const item = row.drCrItems[idx];
                                const col = item.columnKey ? columnMap.get(item.columnKey) : undefined;
                                if (!col) {
                                    return (
                                        <Box key={idx} sx={lineSx}>—</Box>
                                    );
                                }
                                const key = item.columnKey!;
                                return (
                                    <GlColumnLabel
                                        key={idx}
                                        def={col}
                                        fallbackZh={resolveFallbackZh(col)}
                                        showAlias={isAliasVisible(key)}
                                        pageShowAlias={showClientAlias}
                                        onToggleReveal={() => onToggleRevealAlias(key)}
                                        suffix={item.exRate ? ` @ ${item.exRate}` : ''}
                                    />
                                );
                            })}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, whiteSpace: 'nowrap' }}>
                            {indexes.map((idx) => (
                                <Box key={idx} sx={lineSx}>
                                    {row.drCrItems[idx]?.amount
                                        ? coerceAmountInputBySide(side, row.drCrItems[idx].amount)
                                        : '—'}
                                </Box>
                            ))}
                        </TableCell>
                    </React.Fragment>
                );
            })}
            <TableCell sx={{ py: 0.5, width: 40 }} onClick={(e) => e.stopPropagation()}>
                <IconButton size="small" onClick={() => onEdit(row.key)} sx={{ p: 0.25 }} title="Edit row">
                    <EditIcon sx={{ fontSize: 15 }} />
                </IconButton>
                <IconButton
                    size="small"
                    onClick={() => onRemoveRow(row.key)}
                    disabled={!canDelete}
                    sx={{ p: 0.25, color: 'error.main' }}
                    title="Remove row"
                >
                    <DeleteIcon sx={{ fontSize: 15 }} />
                </IconButton>
            </TableCell>
        </TableRow>
    );
});

// ===================== Edit row (full inputs, only one at a time) =====================

interface InvoiceRowEditProps {
    row: InvoiceEntryRow;
    rowIdx: number;
    columnOptions: ColumnOption[];
    columnMap: Map<string, GlColumnDef>;
    typeByCode: Map<string, string>;
    onUpdateRow: (key: string, patch: Partial<InvoiceEntryRow>) => void;
    onUpdateDrCr: (rowKey: string, idx: number, patch: Partial<DrCrItem>) => void;
    onRemoveDrCr: (rowKey: string, idx: number) => void;
    onAddDrCr: (rowKey: string, side: 'dr' | 'cr') => void;
    onBalanceRow: (rowKey: string) => void;
    onDuplicate: (rowKey: string) => void;
    onRemoveRow: (key: string) => void;
    /** Done：只结束编辑，不加行 */
    onDone: () => void;
    /** Esc：只结束编辑，不加行 */
    onCancelEdit?: () => void;
    /** 选完 CR 后自动加下一行 */
    onAdvanceAfterCr: () => void;
    /** 当前是否全表最后一行（中间行改 CR 不加行） */
    isLastRow: boolean;
    onDoneAndNext: () => void;
    onSaveAll: () => void;
    canDelete: boolean;
    fieldHistory: InvoiceFieldHistory;
    periodFrom?: string | null;
    periodTo?: string | null;
}

const editCellSx = { py: 1, verticalAlign: 'top !important' } as const;
const editLineBoxSx = {
    height: DR_CR_LINE_HEIGHT,
    display: 'flex',
    alignItems: 'center',
} as const;
const datePickerWrapSx = {
    width: 140,
    '& .MuiFormControl-root': { m: 0 },
    '& .MuiTextField-root': { m: 0 },
} as const;

const InvoiceRowEdit = React.memo(function InvoiceRowEdit({
    row, rowIdx, columnOptions, columnMap, typeByCode, onUpdateRow, onUpdateDrCr, onRemoveDrCr, onAddDrCr,
    onBalanceRow, onDuplicate, onRemoveRow, onDone, onCancelEdit, onAdvanceAfterCr, isLastRow, onDoneAndNext, onSaveAll, canDelete,
    fieldHistory, periodFrom: periodFromProp, periodTo: periodToProp,
}: InvoiceRowEditProps) {
    const periodFrom = normalizeToDdMmYyyy(periodFromProp) || undefined;
    const periodTo = normalizeToDdMmYyyy(periodToProp) || undefined;
    const rowRef = useRef<HTMLTableRowElement | null>(null);
    const hasWarnings = !!(row.validationWarnings && row.validationWarnings.length > 0);
    const balance = useMemo(
        () => computeRowDrCrBalance(row.drCrItems, columnMap),
        [row.drCrItems, columnMap],
    );

    const handleFieldKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            (onCancelEdit ?? onDone)();
            return;
        }
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            onSaveAll();
            return;
        }
        if ((e.ctrlKey || e.metaKey) && (e.key === 'd' || e.key === 'D')) {
            e.preventDefault();
            e.stopPropagation();
            onDuplicate(row.key);
            return;
        }
        if (e.key === '=' && !(e.ctrlKey || e.metaKey || e.altKey)) {
            const target = e.target as HTMLElement;
            if (target.getAttribute('data-amount-field') === '1') {
                e.preventDefault();
                onBalanceRow(row.key);
                return;
            }
        }
        if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
        if ((e.target as HTMLElement).closest?.('.MuiAutocomplete-root')
            && document.querySelector('.MuiAutocomplete-listbox')) {
            return;
        }
        e.preventDefault();
        const moved = focusNextEditField(rowRef.current, e.target as Element);
        if (!moved) onDoneAndNext();
    }, [onBalanceRow, onDone, onCancelEdit, onDoneAndNext, onDuplicate, onSaveAll, row.key]);

    return (
        <TableRow
            ref={rowRef}
            data-index={rowIdx}
            onKeyDown={handleFieldKeyDown}
            sx={{
                ...(hasWarnings ? { bgcolor: 'warning.50' } : { bgcolor: 'primary.50' }),
                '& > .MuiTableCell-root': { verticalAlign: 'top' },
            }}
        >
            <TableCell sx={{ ...editCellSx, color: 'text.secondary', fontSize: 12 }}>
                <Stack direction="row" alignItems="center" spacing={0.25} sx={editLineBoxSx}>
                    <span>{rowIdx + 1}</span>
                    {hasWarnings && (
                        <Tooltip title={row.validationWarnings!.join('\n')}>
                            <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                        </Tooltip>
                    )}
                </Stack>
            </TableCell>
            <TableCell sx={editCellSx}>
                <Box sx={editLineBoxSx}>
                    <Box sx={datePickerWrapSx}>
                        <DatePicker
                            value={row.date || null}
                            onChange={(v) => onUpdateRow(row.key, { date: v ?? '' })}
                            minDate={periodFrom}
                            maxDate={periodTo}
                            size="small"
                            compact
                            align="left"
                            fullWidth
                        />
                    </Box>
                </Box>
            </TableCell>
            <TableCell sx={editCellSx}>
                <Box sx={editLineBoxSx}>
                    <HistorySuggestField
                        value={row.description}
                        options={fieldHistory.description}
                        onChange={(v) => onUpdateRow(row.key, { description: v })}
                        width={150}
                        placeholder="Description"
                    />
                </Box>
            </TableCell>
            <TableCell sx={editCellSx}>
                <Box sx={editLineBoxSx}>
                    <HistorySuggestField
                        value={row.voucherNo}
                        options={fieldHistory.voucherNo}
                        onChange={(v) => onUpdateRow(row.key, { voucherNo: v })}
                        width={100}
                        placeholder="Voucher No"
                    />
                </Box>
            </TableCell>
            <TableCell sx={editCellSx}>
                <Box sx={editLineBoxSx}>
                    <HistorySuggestField
                        value={row.invoiceNo}
                        options={fieldHistory.invoiceNo}
                        onChange={(v) => onUpdateRow(row.key, { invoiceNo: v })}
                        width={100}
                        placeholder="Invoice No"
                    />
                </Box>
            </TableCell>
            <TableCell sx={editCellSx}>
                <Box sx={editLineBoxSx}>
                    <Box sx={datePickerWrapSx}>
                        <DatePicker
                            value={row.invoiceDate || null}
                            onChange={(v) => onUpdateRow(row.key, { invoiceDate: v ?? '' })}
                            size="small"
                            compact
                            align="left"
                            fullWidth
                        />
                    </Box>
                </Box>
            </TableCell>
            <TableCell sx={editCellSx}>
                <Box sx={editLineBoxSx}>
                    <HistorySuggestField
                        value={row.chequeNo}
                        options={fieldHistory.chequeNo}
                        onChange={(v) => onUpdateRow(row.key, { chequeNo: v })}
                        width={100}
                        placeholder="Cheque No"
                    />
                </Box>
            </TableCell>
            <TableCell sx={editCellSx}>
                <Box sx={editLineBoxSx}>
                    <HistorySuggestField
                        value={row.details}
                        options={fieldHistory.details}
                        onChange={(v) => onUpdateRow(row.key, { details: v })}
                        width={130}
                        placeholder="Details"
                    />
                </Box>
            </TableCell>
            {(['dr', 'cr'] as const).map((side) => {
                const sideIndexes = splitDrCrSides(row.drCrItems)[side];
                const canRemoveSideItem = sideIndexes.length > 1;
                const lineGap = 0.5;
                return (
                <React.Fragment key={side}>
                <TableCell sx={editCellSx}>
                    <Stack spacing={lineGap} alignItems="flex-start">
                        {sideIndexes.map((idx) => {
                            const item = row.drCrItems[idx];
                            return (
                                <Box
                                    key={idx}
                                    sx={{
                                        height: DR_CR_LINE_HEIGHT,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <ColumnSearchSelect
                                        options={filterColumnOptionsBySide(
                                            columnOptions,
                                            columnMap,
                                            side,
                                            typeByCode,
                                            item.columnKey,
                                        )}
                                        value={item.columnKey}
                                        onChange={(columnKey) => {
                                            const def = columnKey ? columnMap.get(columnKey) : undefined;
                                            onUpdateDrCr(row.key, idx, {
                                                columnKey,
                                                ...(!isFxOriginalColumn(def) ? { exRate: '' } : {}),
                                            });
                                            if (shouldAdvanceAfterCrAccountSelect(row.drCrItems, idx, columnKey, isLastRow)) {
                                                window.setTimeout(() => onAdvanceAfterCr(), 0);
                                            }
                                        }}
                                        width={260}
                                        placeholder={side === 'dr' ? 'Search DR account' : 'Search CR account'}
                                    />
                                </Box>
                            );
                        })}
                        <Button
                            size="small"
                            variant="text"
                            startIcon={<AddIcon />}
                            onClick={() => onAddDrCr(row.key, side)}
                            data-edit-skip="1"
                            sx={{ textTransform: 'none', alignSelf: 'flex-start', minWidth: 0, px: 0.5, py: 0 }}
                        >
                            Add {side.toUpperCase()}
                        </Button>
                    </Stack>
                </TableCell>
                <TableCell sx={editCellSx}>
                    <Stack spacing={lineGap} alignItems="flex-end">
                        {sideIndexes.map((idx) => {
                            const item = row.drCrItems[idx];
                            return (
                                <Stack
                                    key={idx}
                                    direction="row"
                                    spacing={0.25}
                                    alignItems="center"
                                    justifyContent="flex-end"
                                    sx={{ height: DR_CR_LINE_HEIGHT, width: '100%' }}
                                >
                                    {isFxOriginalColumn(item.columnKey ? columnMap.get(item.columnKey) : undefined) && (
                                        <InputField
                                            margin="none"
                                            fullWidth={false}
                                            align="right"
                                            placeholder="Rate"
                                            label="Rate"
                                            value={item.exRate ?? ''}
                                            onChange={(e) => onUpdateDrCr(row.key, idx, { exRate: e.target.value })}
                                            sx={{ width: 72, minWidth: 72 }}
                                        />
                                    )}
                                    <InputField
                                        margin="none"
                                        fullWidth={false}
                                        align="right"
                                        type="money"
                                        allowNegative={false}
                                        placeholder="0.00"
                                        value={coerceAmountInputBySide(side, item.amount)}
                                        onChange={(e) => onUpdateDrCr(row.key, idx, {
                                            amount: coerceAmountInputBySide(side, e.target.value),
                                        })}
                                        onBlur={() => {
                                            const normalized = normalizeAmountOnBlur(side, item.amount);
                                            onUpdateDrCr(row.key, idx, { amount: normalized });
                                            // 科目已沿用时只填金额：失焦后借贷平衡 → 自动下一行
                                            if (shouldAdvanceAfterAmountBlur(row.drCrItems, idx, normalized, columnMap)) {
                                                onAdvanceAfterCr();
                                            }
                                        }}
                                        inputProps={{ 'data-amount-field': '1' }}
                                        sx={{ width: 96, minWidth: 96 }}
                                        title={side === 'cr' ? 'CR amount (saved as negative)' : 'DR amount'}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => onRemoveDrCr(row.key, idx)}
                                        disabled={!canRemoveSideItem}
                                        data-edit-skip="1"
                                        sx={{
                                            p: 0.25,
                                            visibility: canRemoveSideItem ? 'visible' : 'hidden',
                                        }}
                                    >
                                        <DeleteIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Stack>
                            );
                        })}
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'success.main',
                                fontWeight: 600,
                                lineHeight: 1.2,
                                pr: 0.5,
                            }}
                        >
                            {side.toUpperCase()} {formatAmountInput(side === 'dr' ? balance.drTotal : balance.crTotal)}
                        </Typography>
                    </Stack>
                </TableCell>
                </React.Fragment>
                );
            })}
            <TableCell sx={editCellSx}>
                <Stack spacing={0.5} alignItems="stretch" sx={{ minWidth: 120 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Button
                            size="small"
                            variant="contained"
                            onClick={onDone}
                            data-edit-nav="1"
                            sx={{ textTransform: 'none', minWidth: 56 }}
                        >
                            Done
                        </Button>
                        <Tooltip title="Duplicate (Ctrl+D)">
                            <IconButton size="small" onClick={() => onDuplicate(row.key)} data-edit-skip="1">
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <IconButton
                            size="small"
                            onClick={() => onRemoveRow(row.key)}
                            disabled={!canDelete}
                            data-edit-skip="1"
                            sx={{ color: 'error.main' }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                    <Tooltip title="Fill opposite amount (=)">
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onBalanceRow(row.key)}
                                disabled={balance.balanced || (balance.drTotal === 0 && balance.crTotal === 0)}
                                data-edit-skip="1"
                                sx={{ textTransform: 'none', width: '100%' }}
                            >
                                Balance
                            </Button>
                        </span>
                    </Tooltip>
                    <Typography
                        variant="caption"
                        sx={{
                            color: balance.balanced ? 'success.main' : 'error.main',
                            fontWeight: 600,
                            textAlign: 'center',
                        }}
                    >
                        {balance.balanced
                            ? 'Balanced'
                            : `Diff ${formatAmountInput(Math.abs(balance.diff))}`}
                    </Typography>
                </Stack>
            </TableCell>
        </TableRow>
    );
});

// ===================== Virtual table body (isolates scroll re-renders) =====================

interface VirtualInvoiceBodyProps {
    rows: InvoiceEntryRow[];
    editingRowKey: string | null;
    columnOptions: ColumnOption[];
    columnMap: Map<string, GlColumnDef>;
    typeByCode: Map<string, string>;
    resolveFallbackZh: (def: GlColumnDef) => string;
    scrollParentRef: React.RefObject<HTMLDivElement | null>;
    onEditRow: (rowKey: string) => void;
    onDoneEdit: () => void;
    onCancelEdit: () => void;
    onAdvanceAfterCr: () => void;
    fieldHistory: InvoiceFieldHistory;
    periodFrom?: string | null;
    periodTo?: string | null;
    onUpdateRow: (key: string, patch: Partial<InvoiceEntryRow>) => void;
    onUpdateDrCr: (rowKey: string, idx: number, patch: Partial<DrCrItem>) => void;
    onRemoveDrCr: (rowKey: string, idx: number) => void;
    onAddDrCr: (rowKey: string, side: 'dr' | 'cr') => void;
    onBalanceRow: (rowKey: string) => void;
    onDuplicate: (rowKey: string) => void;
    onRemoveRow: (key: string) => void;
    onDoneAndNext: () => void;
    onSaveAll: () => void;
    canDelete: boolean;
    showClientAlias: boolean;
    isAliasVisible: (columnKey?: string | null) => boolean;
    onToggleRevealAlias: (columnKey: string) => void;
}

const VirtualInvoiceBody = React.memo(function VirtualInvoiceBody({
    rows, editingRowKey, columnOptions, columnMap, typeByCode, resolveFallbackZh, scrollParentRef,
    onEditRow, onDoneEdit, onCancelEdit, onAdvanceAfterCr, fieldHistory, periodFrom, periodTo, onUpdateRow, onUpdateDrCr, onRemoveDrCr, onAddDrCr, onBalanceRow, onDuplicate,
    onRemoveRow, onDoneAndNext, onSaveAll, canDelete, showClientAlias, isAliasVisible, onToggleRevealAlias,
}: VirtualInvoiceBodyProps) {
    const useVirtual = rows.length > VIRTUALIZE_THRESHOLD;

    const rowVirtualizer = useVirtualizer({
        count: useVirtual ? rows.length : 0,
        getScrollElement: () => scrollParentRef.current,
        estimateSize: (index) => {
            const r = rows[index];
            if (editingRowKey === r?.key) return estimateRowHeight(r) + 40;
            return estimateRowHeight(r);
        },
        overscan: VIRTUAL_OVERSCAN,
        getItemKey: (index) => rows[index]?.key ?? index,
    });

    useLayoutEffect(() => {
        if (!useVirtual) return;
        rowVirtualizer.measure();
    }, [useVirtual, rows.length, editingRowKey, rowVirtualizer]);

    useEffect(() => {
        if (!useVirtual) return;
        const el = scrollParentRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => rowVirtualizer.measure());
        ro.observe(el);
        return () => ro.disconnect();
    }, [useVirtual, scrollParentRef, rowVirtualizer]);

    const renderFullRows = () => (
        rows.map((row, rowIdx) => {
            if (editingRowKey === row.key) {
                return (
                    <InvoiceRowEdit
                        key={row.key}
                        row={row}
                        rowIdx={rowIdx}
                        columnOptions={columnOptions}
                        columnMap={columnMap}
                        typeByCode={typeByCode}
                        onUpdateRow={onUpdateRow}
                        onUpdateDrCr={onUpdateDrCr}
                        onRemoveDrCr={onRemoveDrCr}
                        onAddDrCr={onAddDrCr}
                        onBalanceRow={onBalanceRow}
                        onDuplicate={onDuplicate}
                        onRemoveRow={onRemoveRow}
                        onDone={onDoneEdit}
                        onCancelEdit={onCancelEdit}
                        onAdvanceAfterCr={onAdvanceAfterCr}
                        isLastRow={rowIdx === rows.length - 1}
                        onDoneAndNext={onDoneAndNext}
                        onSaveAll={onSaveAll}
                        canDelete={canDelete}
                        fieldHistory={fieldHistory}
                        periodFrom={periodFrom}
                        periodTo={periodTo}
                    />
                );
            }
            return (
                <InvoiceRowCompact
                    key={row.key}
                    row={row}
                    rowIdx={rowIdx}
                    columnMap={columnMap}
                    resolveFallbackZh={resolveFallbackZh}
                    onEdit={onEditRow}
                    onRemoveRow={onRemoveRow}
                    canDelete={canDelete}
                    showClientAlias={showClientAlias}
                    isAliasVisible={isAliasVisible}
                    onToggleRevealAlias={onToggleRevealAlias}
                />
            );
        })
    );

    // 小列表全量渲染；虚拟列表测量失败时同样降级，避免滚不到末行
    if (!useVirtual || (rowVirtualizer.getVirtualItems().length === 0 && rows.length > 0)) {
        return <TableBody>{renderFullRows()}</TableBody>;
    }

    const virtualItems = rowVirtualizer.getVirtualItems();
    const topPad = virtualItems.length > 0 ? virtualItems[0].start : 0;
    const bottomPad = virtualItems.length > 0
        ? rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
        : 0;

    return (
        <TableBody>
            {topPad > 0 && <tr aria-hidden style={{ height: topPad }} />}
            {virtualItems.map((virtualRow) => {
                const rowIdx = virtualRow.index;
                const row = rows[rowIdx];
                if (!row) return null;
                if (editingRowKey === row.key) {
                    return (
                        <InvoiceRowEdit
                            key={row.key}
                            row={row}
                            rowIdx={rowIdx}
                            columnOptions={columnOptions}
                            columnMap={columnMap}
                            typeByCode={typeByCode}
                            onUpdateRow={onUpdateRow}
                            onUpdateDrCr={onUpdateDrCr}
                            onRemoveDrCr={onRemoveDrCr}
                            onAddDrCr={onAddDrCr}
                            onBalanceRow={onBalanceRow}
                            onDuplicate={onDuplicate}
                            onRemoveRow={onRemoveRow}
                            onDone={onDoneEdit}
                            onCancelEdit={onCancelEdit}
                            onAdvanceAfterCr={onAdvanceAfterCr}
                            isLastRow={rowIdx === rows.length - 1}
                            onDoneAndNext={onDoneAndNext}
                            onSaveAll={onSaveAll}
                            canDelete={canDelete}
                            fieldHistory={fieldHistory}
                            periodFrom={periodFrom}
                            periodTo={periodTo}
                        />
                    );
                }
                return (
                    <InvoiceRowCompact
                        key={row.key}
                        row={row}
                        rowIdx={rowIdx}
                        columnMap={columnMap}
                        resolveFallbackZh={resolveFallbackZh}
                        onEdit={onEditRow}
                        onRemoveRow={onRemoveRow}
                        canDelete={canDelete}
                        showClientAlias={showClientAlias}
                        isAliasVisible={isAliasVisible}
                        onToggleRevealAlias={onToggleRevealAlias}
                    />
                );
            })}
            {bottomPad > 0 && <tr aria-hidden style={{ height: bottomPad }} />}
        </TableBody>
    );
});

// ===================== Main =====================

const StepCInvoiceEntry = React.forwardRef<StepCInvoiceEntryHandle, StepCInvoiceEntryProps>(function StepCInvoiceEntry({
    companyId, annualId, importToken, onSaveSuccess, onSaveError, saving, onSavingChange,
}, ref) {
    const theme = useTheme();
    const { state: ctxState } = useGlInvoiceCreateCtx();
    const [columnDefs, setColumnDefs] = useState<GlColumnDef[]>([]);
    const [colLoading, setColLoading] = useState(false);
    const [rows, setRows] = useState<InvoiceEntryRow[]>([makeEmptyRow()]);
    const [dataLoading, setDataLoading] = useState(false);
    const [dataError, setDataError] = useState<string | null>(null);
    const [summaryPriorYear, setSummaryPriorYear] = useState<Record<string, number>>({});
    const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
    /** DR/CR：标准 GL 叶子 + 本公司 Co. GL 别名（不用模板 columnDefs） */
    const [leafCategories, setLeafCategories] = useState<GlCategory[]>([]);
    const [coGlAccounts, setCoGlAccounts] = useState<GlAccount[]>([]);
    /** Duplicate 时是否保留日期 */
    const [dupKeepDate, setDupKeepDate] = useState(true);
    const {
        showClientAlias, setShowClientAlias, isAliasVisible, toggleRevealKey,
    } = useShowClientAlias();
    const initialLoadDone = useRef(false);
    const scrollParentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let cancelled = false;
        fetchLeafGlCategories()
            .then((leaves) => {
                if (cancelled) return;
                setLeafCategories(
                    (leaves ?? []).filter((c) => !!c.fsCode && !!c.code && c.isActive !== 0),
                );
            })
            .catch(() => {
                if (!cancelled) setLeafCategories([]);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        if (!companyId) {
            setCoGlAccounts([]);
            return;
        }
        listAccountsByCompany(companyId)
            .then((list) => {
                if (!cancelled) setCoGlAccounts(list ?? []);
            })
            .catch(() => {
                if (!cancelled) setCoGlAccounts([]);
            });
        return () => {
            cancelled = true;
        };
    }, [companyId]);

    const fallbackZh = useColumnZhLookup();

    const columnMap = useMemo(
        () => buildDrCrColumnMapFromGlAndCoGl(leafCategories, coGlAccounts),
        [leafCategories, coGlAccounts],
    );

    const columnOptions = useMemo<ColumnOption[]>(
        () => buildDrCrColumnOptions(columnMap, fallbackZh, showClientAlias),
        [columnMap, fallbackZh, showClientAlias],
    );

    const typeByCode = useMemo(() => {
        const m = new Map<string, string>();
        for (const c of leafCategories) {
            if (!c.code || !c.type) continue;
            m.set(c.code, c.type);
            m.set(stripDupSuffix(c.code), c.type);
        }
        return m;
    }, [leafCategories]);

    const validRowCount = useMemo(
        () => rows.filter((r) => r.date || r.description || r.voucherNo).length,
        [rows],
    );

    const canDeleteRows = rows.length > 1;
    const fieldHistory = useMemo(() => collectInvoiceFieldHistory(rows), [rows]);

    const handleEditRow = useCallback((rowKey: string) => {
        setEditingRowKey(rowKey);
    }, []);

    const handleCancelEdit = useCallback(() => {
        setEditingRowKey(null);
    }, []);

    // 换公司 / annual / importToken 时允许重新拉数，避免沿用上家 preload
    useEffect(() => {
        initialLoadDone.current = false;
    }, [companyId, annualId, importToken]);

    useEffect(() => {
        if (ctxState.pendingLegacyMapping) return;
        if (ctxState.preloadedColumnDefs && ctxState.preloadedColumnDefs.length > 0) {
            setColumnDefs(ctxState.preloadedColumnDefs);
            return;
        }
        setColLoading(true);
        fetchColumnDefs()
            .then(setColumnDefs)
            .catch(() => {})
            .finally(() => setColLoading(false));
    }, [ctxState.preloadedColumnDefs, ctxState.pendingLegacyMapping]);

    useEffect(() => {
        if (ctxState.pendingLegacyMapping) {
            setDataLoading(true);
            return;
        }
        if (!companyId || !annualId || initialLoadDone.current) return;

        if (ctxState.preloadStatus === 'done' && ctxState.preloadedGlData) {
            initialLoadDone.current = true;
            setDataLoading(false);
            const preloaded = ctxState.preloadedGlData;
            setRows(preloaded.rows);
            setSummaryPriorYear(
                Object.keys(ctxState.summaryPriorYear || {}).length > 0
                    ? ctxState.summaryPriorYear
                    : preloaded.summaryPriorYear,
            );
            return;
        }

        if (ctxState.preloadStatus === 'loading') {
            setDataLoading(true);
            return;
        }

        if (importToken && ctxState.preloadStatus === 'idle') {
            setDataLoading(true);
            return;
        }

        if (ctxState.preloadStatus === 'error' && importToken) {
            initialLoadDone.current = true;
            setDataError(ctxState.preloadError || 'Preload failed, please re-upload');
            setDataLoading(false);
            return;
        }

        initialLoadDone.current = true;
        if (Object.keys(ctxState.summaryPriorYear || {}).length > 0) {
            setSummaryPriorYear(ctxState.summaryPriorYear);
        }
        const load = async () => {
            setDataLoading(true);
            setDataError(null);
            try {
                if (importToken) {
                    const cached = await fetchGlImportCache(importToken);
                    if (cached?.list?.length) {
                        const defs = (cached.columnDefs?.length ? cached.columnDefs : columnDefs.length ? columnDefs : undefined);
                        setRows(cached.list.map((r) => rowFromGlSheet(r, defs)));
                        const prior = Object.keys(ctxState.summaryPriorYear || {}).length > 0
                            ? ctxState.summaryPriorYear
                            : cached.summaryPriorYear;
                        if (prior) setSummaryPriorYear(prior);
                        return;
                    }
                }
                const res = await fetchGlSheetPage({ companyId, annualId, pageNumber: 1, pageSize: 500 });
                if (res.list?.length) {
                    const defs = columnDefs.length ? columnDefs : undefined;
                    setRows(res.list.map((r) => rowFromGlSheet(r, defs)));
                } else {
                    setRows([makeEmptyRow()]);
                    setEditingRowKey(null);
                }
            } catch (e: any) {
                setDataError(e?.message || 'Failed to load invoice data');
                setRows([makeEmptyRow()]);
            } finally {
                setDataLoading(false);
            }
        };
        load();
    }, [companyId, annualId, importToken, ctxState.preloadStatus, ctxState.preloadedGlData, ctxState.pendingLegacyMapping, ctxState.preloadError, ctxState.summaryPriorYear, columnDefs]);

    const updateRow = useCallback((key: string, patch: Partial<InvoiceEntryRow>) => {
        setRows((prev) => prev.map((r) => r.key === key ? { ...r, ...patch } : r));
    }, []);

    const addRow = useCallback(() => {
        const newRow = makeEmptyRow();
        setRows((prev) => {
            const lastDated = [...prev].reverse().find((r) => (r.date || '').trim());
            if (lastDated?.date) newRow.date = lastDated.date;
            // 同类业务连录：沿用上一笔已选科目（金额清空）
            newRow.drCrItems = copyDrCrAccountsFromRow(findLastRowWithAccounts(prev));
            return [...prev, newRow];
        });
        setEditingRowKey(newRow.key);
    }, []);

    const doneAndNext = useCallback(() => {
        if (!editingRowKey) {
            setEditingRowKey(null);
            return;
        }
        const idx = rows.findIndex((r) => r.key === editingRowKey);
        const next = idx >= 0 ? rows[idx + 1] : undefined;
        if (next) {
            setEditingRowKey(next.key);
            return;
        }
        // 最后一行 Enter：自动加一行继续连录（沿用科目）
        addRow();
    }, [editingRowKey, rows, addRow]);

    const removeRow = useCallback((key: string) => {
        setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));
        setEditingRowKey((k) => (k === key ? null : k));
    }, []);

    const updateDrCr = useCallback((rowKey: string, idx: number, patch: Partial<DrCrItem>) => {
        setRows((prev) => prev.map((r) => {
            if (r.key !== rowKey) return r;
            const items = [...r.drCrItems];
            // 保留原 side：在 CR 列填正数金额时不要跳到 DR
            items[idx] = { ...items[idx], ...patch };
            // DR 金额/科目变化时自动填 CR（1DR+1CR 覆盖；多 CR 只填空格）
            const nextItems = (patch.amount !== undefined || patch.columnKey !== undefined)
                ? autoFillOppositeAmount(items, idx, columnMap)
                : items;
            return { ...r, drCrItems: nextItems };
        }));
    }, [columnMap]);

    const addDrCr = useCallback((rowKey: string, side: 'dr' | 'cr') => {
        setRows((prev) => prev.map((r) => {
            if (r.key !== rowKey) return r;
            return { ...r, drCrItems: [...r.drCrItems, makeEmptyDrCrItem(side)] };
        }));
    }, []);

    const removeDrCr = useCallback((rowKey: string, idx: number) => {
        setRows((prev) => prev.map((r) => {
            if (r.key !== rowKey) return r;
            const removed = r.drCrItems[idx];
            const side = removed?.side ?? 'dr';
            const items = r.drCrItems.filter((_, i) => i !== idx);
            const sides = splitDrCrSides(items);
            if (sides[side].length === 0) items.push(makeEmptyDrCrItem(side));
            return { ...r, drCrItems: items };
        }));
    }, []);

    const balanceRow = useCallback((rowKey: string) => {
        setRows((prev) => prev.map((r) => {
            if (r.key !== rowKey) return r;
            return { ...r, drCrItems: balanceRowDrCrItems(r.drCrItems, columnMap) };
        }));
    }, [columnMap]);

    const duplicateRow = useCallback((rowKey: string) => {
        setRows((prev) => {
            const idx = prev.findIndex((r) => r.key === rowKey);
            if (idx < 0) return prev;
            const copy = duplicateInvoiceEntryRow(prev[idx], {
                clearAmounts: true,
                keepDate: dupKeepDate,
            });
            const next = [...prev];
            next.splice(idx + 1, 0, copy);
            setEditingRowKey(copy.key);
            return next;
        });
    }, [dupKeepDate]);

    const handleSave = useCallback(async () => {
        onSavingChange(true);
        try {
            const apiRows = rows
                .filter((r) => r.date || r.description || r.voucherNo)
                .map((r) => {
                    const columnValues: Record<string, number> = {};
                    writeDrCrColumnValues(r.drCrItems, columnValues);
                    const amounts = r.drCrItems
                        .filter((item) => item.columnKey !== null && item.amount !== '')
                        .map((item) => parseFloat(item.amount))
                        .filter((amt) => !isNaN(amt));
                    return {
                        invoiceId: r.invoiceId || undefined,
                        date: r.date || null,
                        description: r.description || null,
                        voucherNo: r.voucherNo || null,
                        invoiceNo: r.invoiceNo || null,
                        invoiceDate: r.invoiceDate || null,
                        chequeNo: r.chequeNo || null,
                        details: r.details || null,
                        price: amounts.length > 0 ? Math.abs(Math.max(...amounts)) : null,
                        columnValues,
                    };
                });

            if (apiRows.length === 0) {
                onSaveError('No valid rows to save. Please fill in at least Date, Description or Voucher No.');
                return;
            }

            const allNewRows = apiRows.every(r => !r.invoiceId);
            const result = await saveGlPreview({
                companyId, annualId, rows: apiRows as any,
                ...(Object.keys(summaryPriorYear).length > 0 ? {
                    summaryPriorYear,
                    summaryPriorYearRemarks: ctxState.summaryPriorYearRemarks,
                } : {}),
                ...(ctxState.preloadedColumnMetaMap && Object.keys(ctxState.preloadedColumnMetaMap).length > 0
                    ? { columnMetaMap: ctxState.preloadedColumnMetaMap }
                    : {}),
                // Create Stepper / Excel 导入：重复导入覆盖旧数据（非 GlDetail 增量编辑）
                replaceExisting: !!(importToken || ctxState.preloadedGlData || ctxState.preloadedColumnMetaMap || allNewRows),
            });
            onSaveSuccess(result);
        } catch (e: any) {
            onSaveError(e?.message || 'Save failed');
        } finally {
            onSavingChange(false);
        }
    }, [rows, companyId, annualId, summaryPriorYear, ctxState.summaryPriorYearRemarks, ctxState.preloadedColumnMetaMap, importToken, ctxState.preloadedGlData, onSaveSuccess, onSaveError, onSavingChange]);

    useImperativeHandle(ref, () => ({ triggerSave: handleSave }), [handleSave]);

    if (dataLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {dataError && <Alert severity="error" sx={{ mb: 1 }}>{dataError}</Alert>}

            {ctxState.mappingSummary && (
                <Alert severity="success" sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                        Column mapping confirmed
                    </Typography>
                    <Typography variant="body2" component="div">
                        {ctxState.mappingSummary.mappedColumns} columns mapped ·{' '}
                        {ctxState.mappingSummary.totalRows} transaction rows ·{' '}
                        {ctxState.mappingSummary.rowsWithAmount} rows with amounts
                    </Typography>
                </Alert>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" useFlexGap gap={1}>
                <Stack spacing={0.25}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        Invoice Entries ({rows.length} rows
                        {validRowCount !== rows.length ? `, ${validRowCount} valid` : ''})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Click a row to edit · Enter next · Esc done · = balance · Ctrl+D duplicate · Ctrl+Enter save
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={showClientAlias}
                                onChange={(e) => setShowClientAlias(e.target.checked)}
                            />
                        }
                        label={<Typography variant="caption">Show client names</Typography>}
                        sx={{ mr: 0, ml: 0 }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={dupKeepDate}
                                onChange={(e) => setDupKeepDate(e.target.checked)}
                            />
                        }
                        label={<Typography variant="caption">Dup keep date</Typography>}
                        sx={{ mr: 0, ml: 0 }}
                    />
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addRow} sx={{ textTransform: 'none' }}>
                        Add Row
                    </Button>
                </Stack>
            </Stack>

            <Box
                ref={scrollParentRef}
                sx={{
                    overflow: 'auto',
                    maxHeight: 'calc(100vh - 380px)',
                    minHeight: 280,
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                {/* overflow hidden：避免 TableContainer 再出一条滚动条，虚拟列表只认外层 scrollParent */}
                <TableContainer component={Paper} sx={{ boxShadow: theme.shadows[1], overflow: 'hidden' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell sx={{ width: 30, fontWeight: 600 }}>#</TableCell>
                                <TableCell sx={{ minWidth: 100, fontWeight: 600 }}>Date</TableCell>
                                <TableCell sx={{ minWidth: 140, fontWeight: 600 }}>Description</TableCell>
                                <TableCell sx={{ minWidth: 90, fontWeight: 600 }}>Voucher No</TableCell>
                                <TableCell sx={{ minWidth: 90, fontWeight: 600 }}>Invoice No</TableCell>
                                <TableCell sx={{ minWidth: 100, fontWeight: 600 }}>Invoice Date</TableCell>
                                <TableCell sx={{ minWidth: 90, fontWeight: 600 }}>Cheque No</TableCell>
                                <TableCell sx={{ minWidth: 120, fontWeight: 600 }}>Details</TableCell>
                                <TableCell sx={{ minWidth: 280, fontWeight: 600 }}>
                                    DR
                                    {colLoading && <CircularProgress size={12} sx={{ ml: 1 }} />}
                                </TableCell>
                                <TableCell sx={{ minWidth: 210, fontWeight: 600 }} align="right">Amount</TableCell>
                                <TableCell sx={{ minWidth: 280, fontWeight: 600 }}>CR</TableCell>
                                <TableCell sx={{ minWidth: 210, fontWeight: 600 }} align="right">Amount</TableCell>
                                <TableCell sx={{ width: 110, fontWeight: 600 }} />
                            </TableRow>
                        </TableHead>
                        <VirtualInvoiceBody
                            rows={rows}
                            editingRowKey={editingRowKey}
                            columnOptions={columnOptions}
                            columnMap={columnMap}
                            typeByCode={typeByCode}
                            resolveFallbackZh={fallbackZh}
                            scrollParentRef={scrollParentRef}
                            onEditRow={handleEditRow}
                            onDoneEdit={handleCancelEdit}
                            onCancelEdit={handleCancelEdit}
                            onAdvanceAfterCr={addRow}
                            fieldHistory={fieldHistory}
                            periodFrom={ctxState.selectedAnnual?.periodFrom}
                            periodTo={ctxState.selectedAnnual?.periodTo}
                            onUpdateRow={updateRow}
                            onUpdateDrCr={updateDrCr}
                            onRemoveDrCr={removeDrCr}
                            onAddDrCr={addDrCr}
                            onBalanceRow={balanceRow}
                            onDuplicate={duplicateRow}
                            onRemoveRow={removeRow}
                            onDoneAndNext={doneAndNext}
                            onSaveAll={() => { void handleSave(); }}
                            canDelete={canDeleteRows}
                            showClientAlias={showClientAlias}
                            isAliasVisible={isAliasVisible}
                            onToggleRevealAlias={toggleRevealKey}
                        />
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
});

export default StepCInvoiceEntry;
